import { i18n, type SupportedLanguage } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import type { AiAssistant } from "@/types/ai/Assistant";
import { Assistant } from "./Assistant";

/**
 * Reply Assistant - Provides simple answers to questions
 */
export class ReplyAssistant extends Assistant {
  private language: SupportedLanguage;

  constructor() {
    super();
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: this.language,
    };
  }

  getSystemPrompt(): string {
    return `
      You are an AI assistant that provides helpful answers to user questions.

      ## YOUR ROLE
      Answer user questions clearly and concisely. You do not need to access data or perform actions - just provide informative answers.

      ## RESPONSE STYLE
      - Be clear and concise
      - Provide accurate information
      - Be conversational and friendly
      - Always respond in the selected i18n language (see CRITICAL LANGUAGE REQUIREMENT above)

      ## EXAMPLES
      User: "What is a transformer?"
      → Explain what transformers are in the context of data processing

      User: "What does geocoding mean?"
      → Explain geocoding and how it's used in Atlasio
    `;
  }

  async execute(prompt: string): Promise<void> {
    const { showNotification } = useAiNotificationStore.getState();

    const response = await Assistant.sendPrompt<string>(prompt, this.config());

    if (response.error) {
      showNotification(response.error, true);
      return;
    }

    if (response.reply) {
      showNotification(response.reply, false);
    }
  }
}
